import { useEffect, useState } from 'react';
import { FormTextInput } from '@portal_components/components/FormTextInput';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
//import { PageHeading } from '@portal_components/components/PageHeading';
import { FormButton } from '@portal_components/components/FormButton';
import { EHttpMethod } from './hooks/usePortalQuery';
import { normalizeMpan, makeMpanChecks } from '@portal_components/validation/validationUtils';
import { useIsEnv } from './hooks/useIsEnv';
import { FormRadioButtonGroup } from '@portal_components/components/FormRadioButtonGroup';
import axios from 'axios';
import { EnvironmentVariables } from './EnvironmentVariables';
import { FormTextArea } from '@portal_components/components/FormTextArea';

type S98FlagRequestResponse = {
  mpan: string;
  s98_flag: boolean;
};

type S98SearchFormValues = {
  mpan?: string;
  mpanList?: string;
  method: EHttpMethod;
};

export const S98FlagsView = () => {
  const { isLocalHost } = useIsEnv();
  const [requestMethod, setRequestMethod] = useState<EHttpMethod>(EHttpMethod.GET);
  const [isResultLoading, setIsResultLoading] = useState(false);
  const [requestResult, setRequestResult] = useState<S98FlagRequestResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const validationSchema = Yup.object<S98SearchFormValues>({
    mpan: Yup.string().when(['method'], ([method], schema) => {
      if (method !== EHttpMethod.GET) return schema.optional();
      return schema
        .transform(value => (value ? normalizeMpan(value) : value))
        .required('MPAN is required')
        .matches(/^[0-9]{13}$/, 'MPAN must be 13 digits')
        .test('mpan-checksum', 'MPAN checksum is invalid', makeMpanChecks(isLocalHost));
    }),
    mpanList: Yup.string().when(['method'], ([method], schema) => {
      if (method == EHttpMethod.GET) return schema.optional();
      return schema.required('MPAN list is required');
    })
  });

  const methods = useForm<S98SearchFormValues>({
    defaultValues: {
      method: EHttpMethod.GET
    },
    resolver: yupResolver<S98SearchFormValues>(validationSchema as any)
  });

  const [selectedMode] = methods.watch(['method', 'mpan', 'mpanList']);

  const splitAndValidateMpanList = (mpanList: string): string[] => {
    const mpans = mpanList.split('\n').map(mpan => mpan.trim());
    const invalidMpans = mpans.filter(mpan => !/^[0-9]{13}$/.test(mpan) || !makeMpanChecks(isLocalHost)(mpan));
    if (invalidMpans.length > 0) {
      methods.setError('mpanList', {
        type: 'manual',
        message: `Invalid MPAN(s): ${invalidMpans.join(', ')}`
      });
      throw new Error(`Invalid MPANs: ${invalidMpans.join(', ')}`);
    }
    return mpans;
  };

  const onSubmit = async (data: S98SearchFormValues) => {
    setErrorMessage(null);
    setRequestMethod(data.method);
    setIsResultLoading(true);
    setRequestResult(null);

    const path = `${EnvironmentVariables.ApiUrl}/s98-flag`;
    let body = undefined;
    let response;

    if (data.method === EHttpMethod.GET) {
      response = await axios
        .get<S98FlagRequestResponse>(`${path}/${data.mpan}`, {
          headers: {
            'Content-Type': 'application/json'
          },
          withCredentials: true
        })
        .catch(error => error.response);
    } else {
      const queryCallback = data.method === EHttpMethod.POST ? axios.post : axios.put;
      try {
        body = { mpans: splitAndValidateMpanList(data.mpanList || '') };
      } catch (error) {
        setIsResultLoading(false);
        return;
      }

      response = await queryCallback(path, body, {
        headers: {
          'Content-Type': 'application/json'
        },
        withCredentials: true
      }).catch(error => error.response);
    }

    if (response.status !== 200) {
      setErrorMessage(response.data || 'An error occurred while processing the request.');
      setIsResultLoading(false);
      return;
    }

    // Clear the MPAN list after a successful request
    data.mpanList = '';

    // Clear the MPAN input field after a successful request
    data.mpan = '';

    setRequestResult(response?.data || null);
    setIsResultLoading(false);
  };

  useEffect(() => {
    setRequestResult(null);
    setErrorMessage(null);
  }, [selectedMode]);

  return (
    <div>
      {/* <PageHeading
        title="S98 Flags"
        navigationButtons={[
          {
            title: 'Back',
            path: '/',
            icon: '/back.svg',
            className: 'w-6 h-6'
          }
        ]}
      /> */}
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="flex flex-col item-center justify-center bg-elevation-1 p-4 gap-2 border-b-[2px] border-b-secondary-deep-blue-80 shadow-inner px-16">
            <p className="text-xl text-center">ADS Actual Estimate Flag</p>
            <div className="flex flex-col justify-center items-center gap-2">
              {/* --> Business didn't want "add flag" "remove flag" so they've been replaced but for our puposes the flag operations are still represented by POST and DELETE and its what its actually doing to the dynamo records. */}
              <FormRadioButtonGroup
                name="method"
                title="Select Operation"
                className="lg:max-w-[800px]"
                hideErrorControl
                buttons={[
                  {
                    title: 'Search',
                    value: EHttpMethod.GET
                  },
                  {
                    title: 'Accept Estimates',
                    value: EHttpMethod.POST
                  },
                  {
                    title: 'Actual Data Only',
                    value: EHttpMethod.DELETE
                  }
                ]}
                readonly={isResultLoading}
              />
              {selectedMode === EHttpMethod.GET ? (
                <FormTextInput className="lg:max-w-[800px]" title="MPAN" name="mpan" mandatory readonly={isResultLoading} />
              ) : (
                <FormTextArea className="lg:max-w-[800px]" title="MPANs (one per line)" name="mpanList" mandatory readonly={isResultLoading} />
              )}
              <FormButton type="submit" primary className="mb-2" disabled={isResultLoading}>
                {selectedMode === EHttpMethod.GET ? 'Search' : selectedMode === EHttpMethod.POST ? 'Accept Estimates' : 'Actual Data Only'}
              </FormButton>
            </div>
          </div>
        </form>
      </FormProvider>
      {isResultLoading && (
        <div className="flex flex-col bg-elevation-2 animate-pulse-elevation1">
          <p className="text-center text-lg py-4">Loading...</p>
        </div>
      )}
      {(requestResult || errorMessage) && !isResultLoading && (
        <>
          {requestMethod === EHttpMethod.GET && requestResult ? (
            <div className={`flex flex-col bg-elevation-2 py-4 gap-4 border-b-4 ${requestResult.s98_flag ? 'border-b-feedback-green' : 'border-b-feedback-orange'}`}>
              <p className="text-center text-lg">MPAN: {requestResult.mpan}</p>
              <p className="text-center text-lg">{requestResult.s98_flag ? 'Has ADS Flag' : 'No ADS Flag Set'}</p>
            </div>
          ) : (
            <div className={`flex flex-col bg-elevation-2 py-4 gap-4 border-b-4 ${errorMessage ? 'border-b-feedback-red' : 'border-b-feedback-green'}`}>
              <p className="text-center text-lg">
                {errorMessage ? `Error ${requestMethod === EHttpMethod.POST ? 'adding' : 'removing'} ADS Flag` : `ADS Flag ${requestMethod === EHttpMethod.POST ? 'added' : 'removed'} successfully`}
              </p>
              {errorMessage && <p className="text-center text-lg text-feedback-red">{errorMessage}</p>}
            </div>
          )}
        </>
      )}
    </div>
  );
};
